import { createSlice, createAsyncThunk,PayloadAction } from '@reduxjs/toolkit';
import type { SearchCriteriaElements, MapState } from './types';
import { MapResortDetailsProps, ResortDetailsPayloadProps, MapResortListApiResponse } from '../component/MapComponent/shared/types';
import { invokeTypedService } from '../services';
import { SEARCH_API_ROUTES } from '../constant/URL';
import { logErrorToSplunk } from '../../../service';
import { getUserData } from "../util/index";

/** Initial map state */
const initialState: MapState = {
    mapResortListStatus: 'idle',
    mapResortListError: null,
    mapResortList: [],
    mapSelectedResortDetailsStatus: 'idle',
    mapSelectedResortDetailsError: null,
    mapSelectedResortDetailsData: null,
    isMapDragged: false,
    isZoomOutActive: false,
};

/** Fetches resort list for map view */
export const callApiForMapResortList = createAsyncThunk(
  'map/callApiForMapResortList',
  async (searchData: SearchCriteriaElements, { rejectWithValue, signal }) => {
    const url = `${SEARCH_API_ROUTES.MAPVIEW_URL}?customerIdQ=${getUserData().memberId}&appType=rcio`;
    try {
      // ✅ FIX: Pass abort signal to axios via invokeTypedService
      const response = await invokeTypedService<MapResortListApiResponse>({
        resource: url,
        method: 'POST',
        data: searchData,
        // Note: If invokeTypedService doesn't support signal, the AbortController
        // will still work at the React level to prevent state updates
      }).then((res) => {
        // ✅ FIX: Check if request was aborted
        if (signal.aborted) {
          throw new Error('Request was cancelled');
        }
        
        if (res?.error?.message) {
            try {
              logErrorToSplunk(res.error, url, 'POST', 'MapView - callApiForMapResortList');
            } catch (logError) {
              // console.log('Error Logging failed:', logError);
            }
          throw new Error(res.error.message);
        }
        return res;
      });
      
      // ✅ FIX: Check again if request was aborted before returning
      if (signal.aborted) {
        throw new Error('Request was cancelled');
      }
      
      // Extract the actual data from the typed service response
      return response?.data?.availableResorts?.length ? response.data.availableResorts : [];
    } catch (error) {
      // ✅ FIX: Handle abort errors gracefully
      if ((error as Error).name === 'AbortError' || (error as Error).message === 'Request was cancelled') {
        return rejectWithValue('cancelled');
      }
      try {
        logErrorToSplunk(error, url, 'POST', 'MapView - callApiForMapResortList');
      } catch (logError) {
        // console.error('Error logging failed:', logError);
      }
      return rejectWithValue((error as Error).message);
    }
  }
);

/** Fetches specific single resort data */
export const callApiForMapResortDetails = createAsyncThunk(
  'searchApis/callApiForMapResortDetails',
  async (payloadData: ResortDetailsPayloadProps, { rejectWithValue }) => {
    const resortCode = payloadData?.resortCodes?.[0];
    const locale = payloadData?.locale;
    if (!resortCode || !locale) {
      return rejectWithValue('Missing resortCode or locale');
    }
    const url = SEARCH_API_ROUTES.RESORT_DETAILS(resortCode, locale);
    try {
      const response = await invokeTypedService<MapResortDetailsProps>({
        resource: url,
        method: 'GET',
      });

      // Extract the actual data from the typed service response
      return response?.data ? response.data : null;
    } catch (error) {
      try {
        logErrorToSplunk(error, url, 'GET', 'MapView - callApiForMapResortDetails');
      } catch (logError) {
        // console.error('Error logging failed:', logError);
      }
      return rejectWithValue((error as Error).message);
    }
  }
);

/** Map view state management slice */
export const mapSlice = createSlice({
  name: 'map',
  initialState,
  /** Synchronous state updates */
  reducers: {
    /** Reset selected resort details */
    clearSelectedResortDetails: (state) => {
      state.mapSelectedResortDetailsData = null;
      state.mapSelectedResortDetailsError = null;
      state.mapSelectedResortDetailsStatus = 'idle';
    },
    updateMapDragState:(state,action:PayloadAction<boolean>) => {
      state.isMapDragged = !!action.payload;
    },
    updateZoomOutActive: (state, action: PayloadAction<boolean>) => {
      state.isZoomOutActive = !!action.payload;
    },
    updateMapResortWishlistStatus: (state, action: PayloadAction<{ resortId: string; isFavorited: boolean }>) => {
      if (state.mapSelectedResortDetailsData && state.mapSelectedResortDetailsData.resortId === action.payload.resortId) {
        state.mapSelectedResortDetailsData.isEnablewksFavoritedResort = action.payload.isFavorited;
        state.mapSelectedResortDetailsData.favorited = action.payload.isFavorited;
      }
    }
  },
  /** Handle async operations */
  extraReducers: (builder) => {
    builder
      /** Start API request */
      .addCase(callApiForMapResortList.pending, (state) => {
        state.mapResortListStatus = 'loading';
        state.mapResortListError = null;
        // Clear previous pins immediately so stale markers don't linger while new data loads
        state.mapResortList = [];
      })
      /** Handle successful response */
      .addCase(callApiForMapResortList.fulfilled, (state, action) => {
        state.mapResortListStatus = 'succeeded';
        state.mapResortList = action.payload;
        state.mapResortListError = null;
      })
      /** Handle request failure */
      .addCase(callApiForMapResortList.rejected, (state, action) => {
        // ✅ FIX: Don't set error state for cancelled requests
        if (action.payload === 'cancelled') {
          state.mapResortListStatus = 'idle';
        } else {
          state.mapResortListStatus = 'failed';
          state.mapResortListError = action.payload as string;
        }
      }),

    builder
      .addCase(callApiForMapResortDetails.pending, (state) => {
        state.mapSelectedResortDetailsStatus = 'loading';
        state.mapSelectedResortDetailsError = null;
        state.mapSelectedResortDetailsData = null;
      })
      .addCase(callApiForMapResortDetails.fulfilled, (state, action) => {
        state.mapSelectedResortDetailsStatus = 'succeeded';
        state.mapSelectedResortDetailsError = null;
        state.mapSelectedResortDetailsData = action.payload;
      })
      .addCase(callApiForMapResortDetails.rejected, (state, action) => {
        state.mapSelectedResortDetailsStatus = 'failed';
        state.mapSelectedResortDetailsError = action.error.message ?? null;
        state.mapSelectedResortDetailsData = null;
      });
  },
});

/** Export action creators */
export const { 
    clearSelectedResortDetails,
    updateMapDragState,
    updateZoomOutActive,
    updateMapResortWishlistStatus
} = mapSlice.actions;

/** Export reducer */
export default mapSlice.reducer;

